import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error' | 'invalid-token'>('idle');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('invalid-token');
      setMessage('Μη έγκυρο token επαναφοράς κωδικού');
    }
  }, [token]);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Ο κωδικός πρέπει να περιέχει τουλάχιστον ένα μικρό γράμμα';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Ο κωδικός πρέπει να περιέχει τουλάχιστον ένα κεφαλαίο γράμμα';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Ο κωδικός πρέπει να περιέχει τουλάχιστον έναν αριθμό';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setStatus('error');
      setMessage('Μη έγκυρο token επαναφοράς κωδικού');
      return;
    }

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setStatus('error');
      setMessage(passwordError);
      return;
    }

    // Check password confirmation
    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Οι κωδικοί δεν ταιριάζουν');
      return;
    }

    setIsLoading(true);
    setStatus('idle');
    
    try {
      const response = await apiClient.post('/auth?action=reset-password', {
        token,
        newPassword: password
      });

      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Ο κωδικός σας άλλαξε επιτυχώς! Μπορείτε τώρα να συνδεθείτε.',
              type: 'success' 
            }
          });
        }, 3000);
      } else {
        setStatus('error');
        setMessage(response.data.message);
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      
      if (error.response?.data?.message) {
        const errorMessage = error.response.data.message;
        
        if (errorMessage.includes('λήξει') || errorMessage.includes('άκυρο')) {
          setStatus('invalid-token');
        } else {
          setStatus('error');
        }
        
        setMessage(errorMessage);
      } else {
        setStatus('error');
        setMessage('Σφάλμα κατά την επαναφορά του κωδικού');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/(?=.*[a-z])/.test(password)) strength++;
    if (/(?=.*[A-Z])/.test(password)) strength++;
    if (/(?=.*\d)/.test(password)) strength++;
    if (/(?=.*[!@#$%^&*(),.?":{}|<>])/.test(password)) strength++;
    
    return strength;
  };

  const passwordStrength = getPasswordStrength(password);
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
  const strengthLabels = ['Πολύ αδύναμος', 'Αδύναμος', 'Μέτριος', 'Καλός', 'Πολύ καλός'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-medical-50 to-clinical-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 transition-colors duration-300">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-medical-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Lock className="w-8 h-8 text-white" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">
              Νέος Κωδικός
            </h1>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              Εισάγετε τον νέο σας κωδικό
            </p>
          </div>

          {/* Success State */}
          {status === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <div>
                  <p className="text-green-700 font-medium">
                    {message}
                  </p>
                  <p className="text-green-600 text-sm mt-1">
                    Θα μεταφερθείτε στη σελίδα σύνδεσης σε λίγα δευτερόλεπτα...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-red-700 font-medium">
                  {message}
                </p>
              </div>
            </div>
          )}

          {/* Invalid Token State */}
          {status === 'invalid-token' && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <XCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <div>
                  <p className="text-orange-700 font-medium">
                    {message}
                  </p>
                  <p className="text-orange-600 text-sm mt-1">
                    Παρακαλούμε ζητήστε νέο link επαναφοράς κωδικού.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          {status !== 'invalid-token' && status !== 'success' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                  Νέος Κωδικός
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500 dark:bg-gray-700 dark:text-white transition-colors duration-300"
                    placeholder="Εισάγετε νέο κωδικό"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {password && (
                  <div className="mt-2">
                    <div className="flex space-x-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded ${
                            i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Ασφάλεια κωδικού: {strengthLabels[passwordStrength - 1] || 'Πολύ αδύναμος'}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                  Επιβεβαίωση Κωδικού
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500 dark:bg-gray-700 dark:text-white transition-colors duration-300"
                    placeholder="Επιβεβαιώστε τον κωδικό"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                
                {/* Password Match Indicator */}
                {confirmPassword && (
                  <div className="mt-1">
                    {password === confirmPassword ? (
                      <p className="text-xs text-green-600 flex items-center">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Οι κωδικοί ταιριάζουν
                      </p>
                    ) : (
                      <p className="text-xs text-red-600 flex items-center">
                        <XCircle className="w-3 h-3 mr-1" />
                        Οι κωδικοί δεν ταιριάζουν
                      </p>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || password !== confirmPassword || !password}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r from-medical-500 to-medical-600 hover:from-medical-600 hover:to-medical-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-medical-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Αλλαγή Κωδικού'
                )}
              </button>
            </form>
          )}

          {/* Actions */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-3">
            {status === 'success' && (
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-medical-500 to-medical-600 text-white rounded-lg hover:from-medical-600 hover:to-medical-700 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Συνδεθείτε Τώρα
                </Link>
              </div>
            )}
            
            {status === 'invalid-token' && (
              <div className="text-center space-y-2">
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-medical-500 to-medical-600 text-white rounded-lg hover:from-medical-600 hover:to-medical-700 transition-all duration-200 shadow-md hover:shadow-lg mr-2"
                >
                  Νέο Email Επαναφοράς
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 shadow-md hover:shadow-lg"
                >
                  Επιστροφή στη Σύνδεση
                </Link>
              </div>
            )}
            
            {status !== 'success' && status !== 'invalid-token' && (
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center text-medical-600 hover:text-medical-700 dark:text-medical-400 dark:hover:text-medical-300 transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Επιστροφή στη Σύνδεση
                </Link>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
              Χρειάζεστε βοήθεια;{' '}
              <a 
                href="mailto:support@pathiety.gr" 
                className="text-medical-600 hover:text-medical-700 dark:text-medical-400 dark:hover:text-medical-300 transition-colors duration-200"
              >
                Επικοινωνήστε μαζί μας
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}